import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Grid2 as Grid,
  Divider,
  Alert,
} from '@mui/material';
import { formatTimeSpan } from '../utils/formatters';
import { Add as AddIcon, Share as ShareIcon } from '@mui/icons-material';
import schedulesService from '../services/schedulesService';
import cascadeDeleteService from '../services/cascadeDeleteService';
import placeServicesService from '../services/placeServicesService';
import placeServiceProfessionalsService from '../services/placeServiceProfessionalsService';
import entitiesService from '../services/entitiesService';
import placesService from '../services/placesService';
import { useSchedules } from '../hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import useDialog from '../hooks/useDialog';
import useSnackbar from '../hooks/useSnackbar';
import { useContextualHint } from '../hooks/useContextualHint';
import AppSnackbar from '../components/ui/AppSnackbar';
import { useLocation } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import EmptyState from '../components/ui/EmptyState';
import ErrorState from '../components/ui/ErrorState';
import CascadeDeleteDialog from '../components/ui/CascadeDeleteDialog';
import ScheduleForm from '../components/forms/ScheduleForm';
import ScheduleFilters from '../components/ScheduleFilters';
import ScheduleCard from '../components/ScheduleCard';
import ShareScheduleDialog from '../components/ShareScheduleDialog';
import ShiftForm from '../components/ShiftForm';
import SmartWizard from '../components/wizard/SmartWizard';
import AppointmentFormDialog from '../components/AppointmentFormDialog';
import { trackEvent, trackError, trackFormAbandonment } from '../utils/tracking';
import PageLayout from '../components/ui/PageLayout';

export default function SchedulesPage() {
  const location = useLocation();
  const queryClient = useQueryClient();

  // ✅ Custom Hooks - React Query
  const { 
    schedules, 
    professionals, 
    places, 
    loading, 
    error, 
    reload, 
    create, 
    update, 
    deleteItem 
  } = useSchedules();
  
  const formDialog = useDialog();
  const deleteDialog = useDialog();
  const shiftDialog = useDialog();
  const shareDialog = useDialog();
  const { snackbar, showSuccess, showError, showWarning, hideSnackbar } = useSnackbar();

  // Estados adicionais específicos de schedules
  const [placeProfessionals, setPlaceProfessionals] = useState([]);
  const [ScheduleShifts, setAgendaShifts] = useState({});
  // Espelho de entidades (serviços + vínculos) carregado ao abrir o ShiftForm, para oferecer
  // TODOS os serviços do usuário e permitir vincular "pela culatra" ao salvar.
  const [shiftEntities, setShiftEntities] = useState({
    services: [], placeServices: [], placeServiceProfessionals: [], places: [], professionals: [],
  });
  const [shareUrl, setShareUrl] = useState('');
  
  // Estado para wizard de criação de agenda (SmartWizard unificado — config add-schedule)
  const [showSmartWizard, setShowSmartWizard] = useState(false);
  
  // Estado para diálogo de agendamento
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [appointmentPrefillConfig, setAppointmentPrefillConfig] = useState(null);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterProfessional, setFilterProfessional] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); // '' = todos, exceto excluídas

  // Obter dica contextual
  const hint = useContextualHint('schedules', { items: schedules || [], shifts: ScheduleShifts });

  // ── Qual wizard abrir (B-037) ────────────────────────────────────────────
  // O config 'add-schedule' tem os passos de Locais e Profissionais em mode:'select-only' —
  // só dá para ESCOLHER entre os existentes, não criar. Numa conta sem local ele abriria num
  // seletor vazio, sem saída; foi por isso que o botão vivia desabilitado, tapando o buraco.
  // 'setup-completo' tem Profissionais, Serviços e Locais em 'select-or-create' e termina em
  // ScheduleStep — cria tudo do zero. Então em vez de desabilitar, trocamos o wizard.
  const semLocal = (places?.length ?? 0) === 0;
  const wizardId = semLocal ? 'setup-completo' : 'add-schedule';

  // ⚠️ `schedules` vem de `/schedules?includeDeleted=true` — INCLUI as excluídas. E o estado
  // é a string `status` (Active | Inactive | Scheduled | Expired | Deleted), não um booleano
  // `isActive` (que não existe neste objeto). Derivar daqui, e não do array cru.
  const liveSchedules = useMemo(
    () => (schedules || []).filter((s) => (s.status || 'Active').toLowerCase() !== 'deleted'),
    [schedules]
  );
  const semAgenda = liveSchedules.length === 0;

  // Tem agenda viva, mas nenhuma ATIVA — ninguém consegue marcar. NÃO é estado vazio: as
  // agendas aparecem na lista normalmente; o que falta é dizer que são elas que estão
  // derrubando a página pública. Por isso vira aviso ACIMA da lista, não no lugar dela.
  //
  // Exceção: se as únicas não-ativas forem `Scheduled` (data de início no futuro), isso é
  // programação deliberada do dono — avisar seria acusar alguém de um estado que ele escolheu.
  const nenhumaAtiva = useMemo(() => {
    if (liveSchedules.length === 0) return false;
    const st = (s) => (s.status || 'Active').toLowerCase();
    if (liveSchedules.some((s) => st(s) === 'active')) return false;
    if (liveSchedules.every((s) => st(s) === 'scheduled')) return false;
    return true;
  }, [liveSchedules]);

  // Abrir wizard via querystring (?wizard=1 ou ?wizard=true) -> abre seletor de local
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const wizardParam = params.get('wizard');
    if (wizardParam === '1' || wizardParam === 'true') {
      setShowSmartWizard(true);
      const cleanUrl = location.pathname;
      window.history.replaceState(null, '', cleanUrl);
    }
  }, [location.pathname, location.search]);

  // Carregar turnos quando schedules mudar
  useEffect(() => {
    if (schedules?.length > 0) {
      loadShiftsForAgendas(schedules);
    }
  }, [schedules]);

  const loadAllPlaceProfessionals = async (placesData, SchedulesData, professionalsData) => {
    try {
      const allPlaceProfessionals = [];
      
      // Criar placeProfessionals a partir dos professionalIds de cada place
      placesData.forEach(place => {
        if (place.professionalIds && place.professionalIds.length > 0) {
          place.professionalIds.forEach(profId => {
            const professional = professionalsData.find(p => p.id === profId);
            if (professional) {
              // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
              const existingAgenda = SchedulesData.find(
                a => a.placeId === place.id && a.professionalId === profId
              );
              
              allPlaceProfessionals.push({
                id: existingAgenda?.placeProfessionalId || `${place.id}-${profId}`, // ID real ou temporário
                placeId: place.id,
                placeName: place.name,
                professionalId: profId,
                professionalName: professional.name,
                isActive: existingAgenda?.isActive ?? true
              });
            }
          });
        }
      });
      
      setPlaceProfessionals(allPlaceProfessionals);
    } catch (error) {
      console.error('[SchedulesPage] Erro ao criar placeProfessionals:', error);
      setPlaceProfessionals([]);
    }
  };

  const loadShiftsForAgendas = async (SchedulesData) => {
    try {
      const shiftsMap = {};
      await Promise.all(
        SchedulesData.map(async (schedule) => {
          try {
            const shifts = await schedulesService.getShifts(schedule.id, schedule.status?.toLowerCase() === 'deleted');
            shiftsMap[schedule.id] = shifts;
          } catch (error) {
            console.error(`Erro ao carregar turnos da schedule ${schedule.id}:`, error);
            shiftsMap[schedule.id] = [];
          }
        })
      );
      setAgendaShifts(shiftsMap);
    } catch (error) {
      console.error('Erro ao carregar turnos:', error);
    }
  };

  // ✅ Handlers de CRUD
  // ✅ Callbacks memoizados para cards
  const handleEditAgenda = useCallback((schedule) => {
    formDialog.handleOpen(schedule);
  }, [formDialog]);

  const handleDeleteAgenda = useCallback((schedule) => {
    deleteDialog.handleOpen(schedule);
  }, [deleteDialog]);

  const handleShareAgenda = useCallback((schedule) => {
    const shifts = ScheduleShifts[schedule.id] || [];
    
    if (shifts.length === 0) {
      showWarning('Esta agenda precisa ter turnos e horários configurados para poder ser compartilhada.');
      return;
    }
    
    const url = `https://share.agendario.com.br/schedule/${schedule.id}`;
    setShareUrl(url);
    shareDialog.handleOpen(schedule);
  }, [ScheduleShifts, showWarning, shareDialog]);

  const handleCreate = async (data) => {
    try {
      const result = await create(data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_created', {
        agenda_id: result.id || 'unknown',
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead,
        max_available_days_to_show: data.maxAvailableDaysToShow
      });
      
      showSuccess('Agenda criada com sucesso!');
      await loadShiftsForAgendas(schedules);
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: false,
        professional_id: data.professionalId,
        place_id: data.placeId
      });
      showError('Erro ao criar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleUpdate = async (data) => {
    try {
      await update(formDialog.data.id, data);
      formDialog.handleClose();
      
      const professionalName = professionals.find(p => p.id === data.professionalId)?.name;
      const placeName = places.find(p => p.id === data.placeId)?.name;
      
      trackEvent('agenda_updated', {
        agenda_id: formDialog.data.id,
        professional_name: professionalName,
        place_name: placeName,
        max_booking_days_ahead: data.maxBookingDaysAhead
      });
      
      showSuccess('Agenda atualizada com sucesso!');
    } catch (err) {
      trackError('agenda_save_error', err, {
        is_update: true,
        agenda_id: formDialog.data.id
      });
      showError('Erro ao atualizar agenda: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmit = formDialog.isEdit ? handleUpdate : handleCreate;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link copiado com sucesso!');
    } catch (error) {
      showError('Erro ao copiar link');
    }
  };

  const handleOpenUrl = () => {
    window.open(shareUrl, '_blank');
  };

  // ✅ Handlers de Turnos (Shifts)
  const handleOpenShift = async (schedule, shift = null) => {
    try {
      // ✅ NORMALIZADO: schedule agora tem placeId e professionalId diretamente
      const placeId = schedule.placeId;
      const professionalId = schedule.professionalId;
      const placeProfessionalId = schedule.placeProfessionalId;
      
      if (!placeId) {
        showError('Erro: Local não identificado na agenda');
        return;
      }
      
      if (!professionalId) {
        showError('Erro: Profissional não identificado na agenda');
        return;
      }
      
      // Espelho completo das entidades do usuário (serviços + vínculos) para o ShiftForm.
      // Assim ele oferece TODOS os serviços — inclusive os não vinculados a este local/profissional —
      // e o vínculo é criado "pela culatra" no submit.
      let placeName = schedule.placeName || '';
      try {
        const data = await entitiesService.getAll();
        setShiftEntities({
          services: data?.services || [],
          placeServices: data?.placeServices || [],
          placeServiceProfessionals: data?.placeServiceProfessionals || [],
          places: data?.places || [],
          professionals: data?.professionals || [],
        });
        if (!placeName) {
          placeName = (data?.places || []).find(p => p.id === placeId)?.name || '';
        }
      } catch (entitiesError) {
        console.warn('[SchedulesPage] Falha ao carregar entidades para o ShiftForm:', entitiesError);
        setShiftEntities({ services: [], placeServices: [], placeServiceProfessionals: [], places: [], professionals: [] });
      }

      shiftDialog.handleOpen({
        ...shift,
        ScheduleId: schedule.id,
        schedule,
        placeId,
        professionalId,
        placeProfessionalId,
        placeName,
      });
    } catch (error) {
      console.error('Erro ao abrir turno:', error);
      showError('Erro ao abrir turno.');
    }
  };

  const handleShiftSubmit = async (shiftData) => {
    try {
      const { ScheduleId, id: shiftId, placeId, placeProfessionalId } = shiftDialog.data;

      // "Pela culatra": garante que todo serviço marcado esteja vinculado a ESTE profissional
      // neste local (place_service + place_service_professional) ANTES de gravar o turno.
      // Se algum vínculo falhar, aborta o save (não deixa turno meio-vinculado).
      if (placeId && placeProfessionalId) {
        const existingPsp = shiftEntities.placeServiceProfessionals || [];
        const existingPs = shiftEntities.placeServices || [];
        for (const serviceId of (shiftData.serviceIds || [])) {
          const boundHere = existingPsp.some(
            psp => psp.placeProfessionalId === placeProfessionalId && psp.serviceId === serviceId
          );
          if (boundHere) continue;

          // 1) place_service (serviço oferecido no local)
          let placeServiceId = existingPs.find(ps => ps.placeId === placeId && ps.serviceId === serviceId)?.id;
          if (!placeServiceId) {
            const createdPs = await placeServicesService.create(placeId, { serviceId });
            placeServiceId = createdPs?.id;
          }
          if (!placeServiceId) {
            throw new Error('Não foi possível vincular o serviço ao local.');
          }

          // 2) place_service_professional (profissional oferece o serviço no local)
          await placeServiceProfessionalsService.create(placeId, placeServiceId, { placeProfessionalId });
        }
      }

      if (shiftId) {
        await schedulesService.updateShift(ScheduleId, shiftId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno atualizado com sucesso!');
      } else {
        await schedulesService.createShift(ScheduleId, shiftData);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno criado com sucesso!');
      }

      shiftDialog.handleClose();
      await loadShiftsForAgendas(schedules);
    } catch (error) {
      showError('Erro ao salvar turno: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleShiftDelete = async (shiftId) => {
    // Turno ainda não persistido (form aberto para um turno sem id): nada a excluir.
    // Evita DELETE /schedules/{id}/shifts/undefined → 400.
    if (!shiftId) {
      shiftDialog.handleClose();
      return;
    }
    try {
      const ScheduleId = shiftDialog.data.ScheduleId;
      const path = `/schedules/${ScheduleId}/shifts/${shiftId}`;
      const impact = await cascadeDeleteService.getImpact(path);
      const confirmed = window.confirm(
        `Tem certeza que deseja excluir este turno?\n\n` +
        (impact.impactGroups?.length > 0
          ? `Isso afetará:\n${impact.impactGroups.map(g => `• ${g.displayName}: ${g.count} registros`).join('\n')}`
          : 'Isso afetará os registros relacionados.')
      );
      if (confirmed) {
        await cascadeDeleteService.confirmDelete(path, impact.confirmHash);
        queryClient.invalidateQueries({ queryKey: ['schedules'] });
        showSuccess('Turno excluído com sucesso!');
        shiftDialog.handleClose();
        await loadShiftsForAgendas(schedules);
      }
    } catch (error) {
      if (error.response?.status === 409) {
        showError('A confirmação expirou. Por favor, tente novamente.');
      } else {
        showError('Erro ao excluir turno: ' + (error.response?.data?.message || error.message));
      }
    }
  };

  // ✅ Helpers
  const getProfessionalName = (professionalId) => {
    return professionals.find(p => p.id === professionalId)?.name || 'N/A';
  };

  const getPlaceName = (placeId) => {
    return places.find(p => p.id === placeId)?.name || 'N/A';
  };

  const formatShiftDays = (shift) => {
    const days = [];
    if (shift.monday) days.push('Seg');
    if (shift.tuesday) days.push('Ter');
    if (shift.wednesday) days.push('Qua');
    if (shift.thursday) days.push('Qui');
    if (shift.friday) days.push('Sex');
    if (shift.saturday) days.push('Sáb');
    if (shift.sunday) days.push('Dom');
    return days.join(', ');
  };

  // Alias para manter compatibilidade com código existente (props passadas para componentes)
  const formatTime = formatTimeSpan;

  // ✅ Filtros memoizados
  const filteredAgendas = useMemo(() => {
    return (schedules || []).filter((schedule) => {
      const matchesSearchTerm = !searchTerm ||
        schedule.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        schedule.description?.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesProfessional = !filterProfessional ||
        schedule.professionalId === filterProfessional;

      const matchesPlace = !filterPlace ||
        schedule.placeId === filterPlace;

      // Filtro de status: por padrão esconde excluídas; ao selecionar um status específico, filtra exatamente
      const matchesStatus = filterStatus
        ? schedule.status?.toLowerCase() === filterStatus.toLowerCase()
        : schedule.status?.toLowerCase() !== 'deleted';

      return matchesSearchTerm && matchesProfessional && matchesPlace && matchesStatus;
    });
  }, [schedules, searchTerm, filterProfessional, filterPlace, filterStatus]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterProfessional('');
    setFilterPlace('');
    setFilterStatus('');
  };

  // Separar próprias vs compartilhadas (após filtros)
  const ownSchedules = filteredAgendas.filter(s => !s.isShared);
  const sharedSchedules = filteredAgendas.filter(s => s.isShared);

  const renderScheduleGrid = (scheduleList) => (
    <Grid container spacing={3}>
      {scheduleList.map((schedule) => (
        <Grid size={{ xs: 12, sm: 6, md: 4, xl: 3 }} key={schedule.id}>
          <ScheduleCard
            schedule={schedule}
            professionalName={schedule.professionalName || 'N/A'}
            placeName={schedule.placeName || 'N/A'}
            shifts={ScheduleShifts[schedule.id] || []}
            canWrite={schedule.canWrite ?? true}
            onEdit={handleEditAgenda}
            onDelete={handleDeleteAgenda}
            onShare={handleShareAgenda}
            onAddShift={() => handleOpenShift(schedule)}
            onEditShift={(shift) => handleOpenShift(schedule, shift)}
            onAppointment={handleOpenAppointmentFromSchedule}
            formatShiftDays={formatShiftDays}
            formatTime={formatTime}
          />
        </Grid>
      ))}
    </Grid>
  );

  // ✅ Handlers para wizard de criação de agenda
  const handleSmartWizardSuccess = useCallback(() => {
    setShowSmartWizard(false);
    showSuccess('Agenda criada com sucesso!');
    reload(); // Recarregar lista de agendas
  }, [showSuccess, reload]);

  // Handler para abrir diálogo de agendamento a partir de um schedule
  const handleOpenAppointmentFromSchedule = useCallback((schedule) => {
    setAppointmentPrefillConfig({
      professionalId: schedule.professionalId,
      placeId: schedule.placeId,
    });
    setAppointmentDialogOpen(true);
  }, []);

  // Handler para fechar diálogo de agendamento
  const handleCloseAppointmentDialog = useCallback(() => {
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, []);

  // Handler para sucesso do agendamento
  const handleAppointmentSuccess = useCallback((appointment) => {
    showSuccess('Agendamento criado com sucesso!');
    setAppointmentDialogOpen(false);
    setAppointmentPrefillConfig(null);
  }, [showSuccess]);


  // ✅ Loading/Error States
  if (loading) return <LoadingState message="Carregando agendas..." />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <PageLayout
      themeKey="schedules"
      actionLabel="Nova Agenda"
      onAction={() => setShowSmartWizard(true)}
      hint={hint}
      onHintAction={() => formDialog.handleOpen(null)}
      infoBanner="Configure agendas automáticas vinculando profissionais a locais. A geração automática cria horários disponíveis com base nos turnos configurados."
    >
      {/* Filtros */}
      <ScheduleFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        filterProfessional={filterProfessional}
        onProfessionalChange={setFilterProfessional}
        filterPlace={filterPlace}
        onPlaceChange={setFilterPlace}
        filterStatus={filterStatus}
        onStatusChange={setFilterStatus}
        professionals={professionals}
        places={places}
        onClearFilters={clearFilters}
      />

      {/* Aviso de agendas todas desativadas (B-037).
          Fica ACIMA da lista, não no lugar dela: as agendas existem e continuam visíveis —
          o que faltava era dizer que são elas que derrubam o agendamento da página pública. */}
      {nenhumaAtiva && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Todas as suas agendas estão <strong>desativadas</strong>. Sua página continua no ar, mas
          ninguém consegue marcar horário. Reative uma agenda abaixo para voltar a receber
          agendamentos.
        </Alert>
      )}

      {/* Empty State */}
      {filteredAgendas.length === 0 ? (
        <EmptyState
          icon={() => <Typography fontSize={64}>📅</Typography>}
          title={semAgenda ? 'Você ainda não tem agenda' : 'Nenhuma agenda encontrada'}
          message={
            semAgenda
              ? (semLocal
                  ? 'Sem agenda, ninguém consegue marcar horário na sua página. O assistente cria tudo de uma vez: local, serviço, profissional e a agenda.'
                  : 'Sem agenda, ninguém consegue marcar horário na sua página.')
              : 'Tente ajustar os filtros para encontrar suas agendas.'
          }
          action={
            semAgenda ? (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setShowSmartWizard(true)}>
                {semLocal ? 'Configurar agora' : 'Criar minha agenda'}
              </Button>
            ) : (
              <Button variant="outlined" onClick={clearFilters}>
                Limpar filtros
              </Button>
            )
          }
        />
      ) : (
        <>
          {/* Minhas Agendas */}
          {ownSchedules.length > 0 && (
            <Box mb={sharedSchedules.length > 0 ? 4 : 0}>
              {sharedSchedules.length > 0 && (
                <Typography variant="h6" fontWeight="bold" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                  Minhas Agendas ({ownSchedules.length})
                </Typography>
              )}
              {renderScheduleGrid(ownSchedules)}
            </Box>
          )}

          {/* Compartilhadas comigo */}
          {sharedSchedules.length > 0 && (
            <Box mb={4}>
              {ownSchedules.length > 0 && <Divider sx={{ mb: 3 }} />}
              <Typography variant="h6" fontWeight="bold" color="info.main" gutterBottom sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ShareIcon fontSize="small" />
                Compartilhadas comigo ({sharedSchedules.length})
              </Typography>
              {renderScheduleGrid(sharedSchedules)}
            </Box>
          )}
        </>
      )}

      {/* schedule Form Dialog */}
      <ScheduleForm
        open={formDialog.open}
        onClose={formDialog.handleClose}
        onSubmit={handleSubmit}
        data={formDialog.data}
        professionals={professionals}
        places={places}
      />

      {/* Delete Confirmation Dialog */}
      <CascadeDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDeleted={() => {
          deleteDialog.handleClose();
          showSuccess('Excluído com sucesso!');
          reload();
        }}
        deletePath={`/schedules/${deleteDialog.data?.id}`}
        entityName={deleteDialog.data?.name}
      />

      {/* Share Dialog */}
      <ShareScheduleDialog
        open={shareDialog.open}
        onClose={shareDialog.handleClose}
        shareUrl={shareUrl}
        onCopy={handleCopyUrl}
        onOpen={handleOpenUrl}
      />

      {/* Shift Form Dialog */}
      <ShiftForm
        open={shiftDialog.open}
        onClose={shiftDialog.handleClose}
        onSubmit={handleShiftSubmit}
        onDelete={handleShiftDelete}
        shift={shiftDialog.data}
        ScheduleId={shiftDialog.data?.ScheduleId}
        context={{
          placeId: shiftDialog.data?.placeId,
          professionalId: shiftDialog.data?.professionalId,
          placeProfessionalId: shiftDialog.data?.placeProfessionalId,
          placeName: shiftDialog.data?.placeName,
        }}
        services={shiftEntities.services}
        placeServices={shiftEntities.placeServices}
        placeServiceProfessionals={shiftEntities.placeServiceProfessionals}
        places={shiftEntities.places}
        professionals={shiftEntities.professionals}
      />

      {/* Wizard unificado de criação de agenda (SmartWizard).
          Substitui o antigo ScheduleCreationWizard + seletor de local: o próprio wizard
          escolhe local → profissional → serviços → vínculos → agenda, garantindo que o
          turno nasça com serviço vinculado.

          O config varia com o estado da conta (B-037): 'add-schedule' pressupõe local e
          profissional já existentes (passos em mode:'select-only'); quem não tem local vai
          para 'setup-completo', que cria tudo do zero ('select-or-create'). */}
      <SmartWizard
        open={showSmartWizard}
        wizardId={wizardId}
        onClose={() => setShowSmartWizard(false)}
        onSuccess={handleSmartWizardSuccess}
      />

      {/* Diálogo de Agendamento - auto-suficiente, carrega próprios dados */}
      <AppointmentFormDialog
        open={appointmentDialogOpen}
        onClose={handleCloseAppointmentDialog}
        onSuccess={handleAppointmentSuccess}
        prefillConfig={appointmentPrefillConfig}
      />

      {/* Snackbar */}
      <AppSnackbar snackbar={snackbar} onClose={hideSnackbar} />
    </PageLayout>
  );
}
